import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnimatedProgress } from '../../../shared/hooks/useAnimatedProgress';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Spinner,
  LocationInput,
} from '../../../shared/components/ui';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { formatCurrency, formatSqFt } from '../../../shared/utils/formatters';
import { EstimateTreeEditor } from './EstimateTreeEditor';
import { SupportingDocumentsUploader } from './SupportingDocumentsUploader';
import { DocumentFactsReview } from './DocumentFactsReview';

// Generate temporary IDs for new items
const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Wizard step definitions
const STEPS = {
  PROJECT_DETAILS: 'project_details',
  UPLOAD: 'upload',
  CHOOSE_STARTING_POINT: 'choose_starting_point',
  CSV_PROCESSING: 'csv_processing',
  REVIEW_ESTIMATE: 'review_estimate',
  UPLOAD_DOCUMENTS: 'upload_documents',
  DOC_PROCESSING: 'doc_processing',
  REVIEW_FACTS: 'review_facts',
  COMPLETE: 'complete',
};

// Step metadata for the step indicator
const STEP_CONFIG = [
  { key: STEPS.UPLOAD, label: 'Upload', number: 1 },
  { key: STEPS.PROJECT_DETAILS, label: 'Project', number: 2 },
  { key: STEPS.CSV_PROCESSING, label: 'Processing', number: 2 },
  { key: STEPS.CHOOSE_STARTING_POINT, label: 'Template', number: 3 },
  { key: STEPS.REVIEW_ESTIMATE, label: 'Review', number: 3 },
  { key: STEPS.UPLOAD_DOCUMENTS, label: 'Documents', number: 4 },
  { key: STEPS.DOC_PROCESSING, label: 'Processing', number: 5 },
  { key: STEPS.REVIEW_FACTS, label: 'Facts', number: 6 },
  { key: STEPS.COMPLETE, label: 'Complete', number: 7 },
];

// ============================================================================
// Circular Progress Component (copied from NewEstimateWizard)
// ============================================================================
function CircularProgress({ percent, size = 120, strokeWidth = 8 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#2563eb"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-200 ease-linear"
        />
      </svg>
      {/* Percentage text in center */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{percent.toFixed(0)}%</span>
      </div>
    </div>
  );
}

// ============================================================================
// Drop Zone Component (adapted from EstimateUpload)
// ============================================================================
function DropZone({ onFileSelect, isUploading }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [onFileSelect]);

  const handleFileInput = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [onFileSelect]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`border-2 border-dashed rounded-lg p-6 sm:p-12 text-center transition-colors ${
        isDragging
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
          : 'border-gray-300 dark:border-gray-700 hover:border-gray-400'
      } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <svg
        className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>
      <p className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2">
        {isDragging ? 'Drop your file here' : 'Drag and drop your estimate file'}
      </p>
      <p className="text-gray-500 dark:text-gray-400 mb-4">
        or click to browse
      </p>
      <input
        type="file"
        onChange={handleFileInput}
        accept=".csv,.xlsx,.xls"
        className="hidden"
        id="wizard-file-input"
        disabled={isUploading}
      />
      <label
        htmlFor="wizard-file-input"
        className={`inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 px-4 py-2 text-sm bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-blue-500 cursor-pointer ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        Select File
      </label>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-4">
        Supported formats: CSV, Excel (.xlsx, .xls)
      </p>
    </div>
  );
}

// ============================================================================
// CSV Processing Progress Component
// ============================================================================
function getDefaultStatusMessage(progress) {
  if (progress < 5) return 'Preparing to process...';
  if (progress < 15) return 'Analyzing file structure...';
  if (progress < 25) return 'Extracting project details...';
  if (progress < 80) return 'Extracting line items...';
  if (progress < 95) return 'Validating data...';
  return 'Finalizing...';
}

function CSVProcessingProgress({ progress, displayProgress, fileName, onCancel, currentPassDetail }) {
  return (
    <Card>
      <CardContent className="py-12">
        <div className="flex flex-col items-center text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Processing Your Estimate
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md">
            We're analyzing your file and extracting cost categories and line items.
          </p>

          {/* Circular Progress */}
          <div className="mb-6">
            <CircularProgress percent={displayProgress} size={140} strokeWidth={10} />
          </div>

          {/* File Name */}
          <div className="mb-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">Processing file</span>
            <p className="text-lg font-medium text-blue-600 dark:text-blue-400">{fileName}</p>
          </div>

          {/* Status Message */}
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-8">
            {currentPassDetail || getDefaultStatusMessage(progress)}
          </p>

          {/* Cancel Button */}
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Document Processing Progress Component
// ============================================================================
function DocProcessingProgress({ total, completed, onSkip }) {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Card>
      <CardContent className="py-12">
        <div className="flex flex-col items-center text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Processing Documents
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md">
            We're extracting key information from your supporting documents.
          </p>

          {/* Circular Progress */}
          <div className="mb-6">
            <CircularProgress percent={percent} size={140} strokeWidth={10} />
          </div>

          {/* Document Count */}
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-8">
            {completed} of {total} documents processed
          </p>

          {/* Skip Button */}
          <Button variant="outline" onClick={onSkip}>
            Skip and Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Completion Component
// ============================================================================
function UploadComplete({ estimate, documentCount, onViewEstimate, onUploadAnother }) {
  return (
    <Card>
      <CardContent className="py-12">
        <div className="flex flex-col items-center text-center">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Upload Complete!
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md">
            Your estimate has been successfully processed and saved.
          </p>

          {/* Summary */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 mb-8 text-left max-w-md w-full">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Project Name:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{estimate?.projectName || 'Imported Estimate'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Categories:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{estimate?.categories?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Line Items:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {estimate?.categories?.reduce((sum, c) => sum + (c.lineItems?.length || 0), 0) || 0}
                </span>
              </div>
              {documentCount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Documents:</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{documentCount}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button variant="outline" onClick={onUploadAnother}>
              Upload Another
            </Button>
            <Button onClick={onViewEstimate}>
              View Estimate Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// All Estimates Table Component (adapted from NewEstimateWizard)
// ============================================================================
function AllEstimatesTable({ estimates, search, onSearchChange, onSelect, isLoading }) {
  const filteredEstimates = estimates.filter(e =>
    !search ||
    e.projectName?.toLowerCase().includes(search.toLowerCase()) ||
    e.clientName?.toLowerCase().includes(search.toLowerCase()) ||
    e.jobType?.toLowerCase().includes(search.toLowerCase()) ||
    e.location?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Browse All Estimates</h3>
        <div className="relative w-64">
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search estimates..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-2 text-gray-600 dark:text-gray-300">Loading estimates...</span>
        </div>
      ) : filteredEstimates.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">No estimates found.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
              <tr>
                <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Project</th>
                <th className="hidden md:table-cell text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Type</th>
                <th className="hidden md:table-cell text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Location</th>
                <th className="hidden md:table-cell text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Size</th>
                <th className="text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Total</th>
                <th className="hidden md:table-cell text-center py-2 px-3 font-medium text-gray-700 dark:text-gray-200">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredEstimates.slice(0, 20).map((estimate) => (
                <tr
                  key={estimate.estimateId}
                  onClick={() => onSelect(estimate.estimateId)}
                  className="border-t border-gray-100 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer"
                >
                  <td className="py-2 px-3 font-medium text-gray-900 dark:text-gray-100">
                    <span className="break-words">{estimate.projectName}</span>
                    {/* Mobile-only inline meta so the hidden columns are still scannable. */}
                    <div className="md:hidden mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {estimate.location && <span>{estimate.location}</span>}
                      {estimate.jobType && <Badge variant="blue">{estimate.jobType}</Badge>}
                      <Badge variant={estimate.projectCompleted ? 'green' : 'yellow'}>
                        {estimate.projectCompleted ? 'Complete' : 'Draft'}
                      </Badge>
                    </div>
                  </td>
                  <td className="hidden md:table-cell py-2 px-3">
                    {estimate.jobType && <Badge variant="blue">{estimate.jobType}</Badge>}
                  </td>
                  <td className="hidden md:table-cell py-2 px-3 text-gray-600 dark:text-gray-300">{estimate.location}</td>
                  <td className="hidden md:table-cell py-2 px-3 text-right text-gray-600 dark:text-gray-300">{formatSqFt(estimate.squareFootage)}</td>
                  <td className="py-2 px-3 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatCurrency(estimate.totalCost)}</td>
                  <td className="hidden md:table-cell py-2 px-3 text-center">
                    <Badge variant={estimate.projectCompleted ? 'green' : 'yellow'}>
                      {estimate.projectCompleted ? 'Complete' : 'Draft'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Step Indicator Component
// ============================================================================
function StepIndicator({ currentStep, skippedDocuments, skippedCsvProcessing }) {
  const currentIndex = STEP_CONFIG.findIndex((s) => s.key === currentStep);

  // Determine which steps to show based on what was skipped
  const hiddenSteps = new Set();
  if (skippedCsvProcessing) {
    // Manual entry path: hide CSV processing, show project details + template choice
    hiddenSteps.add(STEPS.CSV_PROCESSING);
  } else {
    // AI processing path: hide project details + template choice, show CSV processing
    hiddenSteps.add(STEPS.PROJECT_DETAILS);
    hiddenSteps.add(STEPS.CHOOSE_STARTING_POINT);
  }
  if (skippedDocuments) {
    hiddenSteps.add(STEPS.DOC_PROCESSING);
    hiddenSteps.add(STEPS.REVIEW_FACTS);
  }
  const stepsToShow = STEP_CONFIG.filter((s) => !hiddenSteps.has(s.key));

  return (
    <div className="flex items-center justify-center mb-6 sm:mb-8 px-2 overflow-x-auto">
      <div className="flex items-center">
        {stepsToShow.map((step, index) => {
          const stepIndex = STEP_CONFIG.findIndex((s) => s.key === step.key);
          const isCurrent = step.key === currentStep;
          const isPast = stepIndex < currentIndex;

          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <div className={`w-6 sm:w-10 h-0.5 ${isPast ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'}`} />
              )}
              <div className="flex flex-col items-center">
                <div
                  className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm flex-shrink-0 ${
                    isCurrent
                      ? 'bg-blue-600 text-white'
                      : isPast
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {isPast ? (
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span className={`text-xs mt-1 font-medium hidden sm:block ${isCurrent ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Main Wizard Component
// ============================================================================
export function EstimateUploadWizard() {
  const navigate = useNavigate();

  // Project details state
  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [projectLocation, setProjectLocation] = useState('');
  const [locationValidated, setLocationValidated] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { uploadId, estimateId }
  const [estimate, setEstimate] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [skippedDocuments, setSkippedDocuments] = useState(false);
  const [skippedCsvProcessing, setSkippedCsvProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Template selection state (for "Start from Existing Estimate")
  const [allEstimates, setAllEstimates] = useState([]);
  const [allEstimatesSearch, setAllEstimatesSearch] = useState('');
  const [isLoadingAllEstimates, setIsLoadingAllEstimates] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [, setSelectedTemplateId] = useState(null);

  // Processing status polling
  const [processingStatus, setProcessingStatus] = useState({ status: 'pending', progress: 0, currentPass: null, currentPassDetail: null });
  const [docProcessingStatus, setDocProcessingStatus] = useState({ total: 0, completed: 0 });
  const pollingRef = useRef(null);

  // Cleanup tracking: refs so unmount closure can access latest values
  const wizardCompletedRef = useRef(false);
  const uploadResultRef = useRef(null);

  // Keep uploadResultRef in sync with state
  useEffect(() => {
    uploadResultRef.current = uploadResult;
  }, [uploadResult]);

  // Animated progress for smooth display
  // Use backend's weighted progress as the driver, with 100 "batches" (1 per %)
  // so the hook smoothly animates between the backend's discrete progress jumps
  const displayProgress = useAnimatedProgress(
    processingStatus.progress,
    100, // totalBatches - treat each 1% as a batch
    processingStatus.progress, // completedBatches - current progress value
    { enabled: currentStep === STEPS.CSV_PROCESSING && isUploading }
  );

  // Clean up polling and pending estimate on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      // If wizard hasn't completed, clean up any pending estimate
      if (!wizardCompletedRef.current && uploadResultRef.current?.estimateId) {
        estimatesApi.deleteEstimate(uploadResultRef.current.estimateId).catch((err) => {
          console.warn('Failed to clean up pending estimate on unmount:', err);
        });
      }
    };
  }, []);

  // Fire-and-forget cleanup of a pending estimate
  const cleanupPendingEstimate = useCallback((estimateId) => {
    if (!estimateId) return;
    estimatesApi.deleteEstimate(estimateId).catch((err) => {
      console.warn('Failed to clean up pending estimate:', err);
    });
  }, []);

  // ============================================================================
  // File Handling
  // ============================================================================
  const handleFileSelect = useCallback((file) => {
    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (!validTypes.includes(file.type) && !validExtensions.includes(extension)) {
      setError('Please select a CSV or Excel file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }

    setSelectedFile(file);
    setError(null);
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
  }, []);

  // ============================================================================
  // Upload and Processing
  // ============================================================================
  const startProcessingPolling = useCallback((estimateId) => {
    // Poll the backend for processing status using the actual estimate ID
    pollingRef.current = setInterval(async () => {
      try {
        const status = await estimatesApi.getEstimateStatus(estimateId);
        setProcessingStatus({
          status: status.status,
          progress: status.progress || 0,
          currentPass: status.currentPass,
          currentPassDetail: status.currentPassDetail,
        });

        if (status.status === 'completed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;

          // Fetch the full estimate with categories and line items
          try {
            const fullEstimate = await estimatesApi.getEstimateById(estimateId);
            setEstimate(fullEstimate);
            // Sync AI-extracted metadata to wizard state for the review step
            setProjectName(fullEstimate.projectName || '');
            setClientName(fullEstimate.clientName || '');
            setProjectLocation(fullEstimate.location || '');
            setIsUploading(false);
            setCurrentStep(STEPS.REVIEW_ESTIMATE);
          } catch (fetchErr) {
            console.error('Failed to fetch estimate:', fetchErr);
            setError('Failed to load processed estimate. Please try again.');
            setCurrentStep(STEPS.UPLOAD);
            setIsUploading(false);
          }
        } else if (status.status === 'failed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setError(status.error || 'Processing failed. Please try again.');
          setCurrentStep(STEPS.UPLOAD);
          setIsUploading(false);
        }
      } catch (err) {
        // Network error - keep polling, might recover
        console.error('Failed to poll status:', err);
      }
    }, 2000); // Poll every 2 seconds
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setCurrentStep(STEPS.CSV_PROCESSING);

    try {
      // Upload the file
      const result = await estimatesApi.uploadEstimate(selectedFile, (progress) => {
        setUploadProgress(progress);
      }, { projectName: '' });

      setUploadResult(result);
      setUploadProgress(100);

      // Start polling for processing status using estimateId (the actual database ID)
      startProcessingPolling(result.estimateId);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setCurrentStep(STEPS.UPLOAD);
      setIsUploading(false);
    }
  }, [selectedFile, startProcessingPolling]);

  const handleManualEntry = useCallback(async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Upload the file but skip AI processing
      const result = await estimatesApi.uploadEstimate(selectedFile, (progress) => {
        setUploadProgress(progress);
      }, { skipProcessing: true, projectName: '' });

      setUploadResult(result);
      setUploadProgress(100);
      setSkippedCsvProcessing(true);
      setIsUploading(false);

      // Go to project details step to collect metadata
      setCurrentStep(STEPS.PROJECT_DETAILS);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  }, [selectedFile]);

  const handleSkipUpload = useCallback(async () => {
    setIsUploading(true);
    setError(null);

    try {
      // Create a pending estimate record without uploading a file.
      // getUploadUrl with skipProcessing creates a pending estimate with
      // import_status="manual_entry" and returns the estimateId.
      // The presigned S3 URL is intentionally unused.
      const response = await estimatesApi.getUploadUrl('manual-entry.csv', 'text/csv', {
        skipProcessing: true,
        projectName: '',
      });
      const data = response.data || response;
      const { estimateId, uploadId } = data;

      setUploadResult({ estimateId: estimateId || uploadId, uploadId });
      setSkippedCsvProcessing(true);
      setIsUploading(false);

      // Go to project details step to collect metadata
      setCurrentStep(STEPS.PROJECT_DETAILS);
    } catch (err) {
      setError(err.message || 'Failed to initialize estimate. Please try again.');
      setIsUploading(false);
    }
  }, []);

  const handleCancelProcessing = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (uploadResult?.estimateId) {
      cleanupPendingEstimate(uploadResult.estimateId);
    }
    setCurrentStep(STEPS.UPLOAD);
    setIsUploading(false);
    setUploadProgress(0);
    setSelectedFile(null);
    setUploadResult(null);
  }, [uploadResult, cleanupPendingEstimate]);

  // ============================================================================
  // Template Selection (Choose Starting Point)
  // ============================================================================
  const handleLoadAllEstimates = useCallback(async () => {
    setIsLoadingAllEstimates(true);
    try {
      const result = await estimatesApi.getEstimates({ limit: 100 });
      setAllEstimates(result.estimates || []);
    } catch (err) {
      console.error('Failed to load estimates:', err);
      setError('Failed to load existing estimates.');
    } finally {
      setIsLoadingAllEstimates(false);
    }
  }, []);

  // Load all estimates when entering the choose starting point step
  useEffect(() => {
    if (currentStep === STEPS.CHOOSE_STARTING_POINT) {
      handleLoadAllEstimates();
    }
  }, [currentStep, handleLoadAllEstimates]);

  const handleStartFromScratch = useCallback(() => {
    const emptyEstimate = {
      estimateId: uploadResult.estimateId,
      projectName: projectName.trim(),
      categories: [],
      totalCost: 0,
    };

    setEstimate(emptyEstimate);
    setCurrentStep(STEPS.REVIEW_ESTIMATE);
  }, [uploadResult, projectName]);

  const handleSelectExistingEstimate = useCallback(async (templateEstimateId) => {
    if (isLoadingTemplate) return;

    setSelectedTemplateId(templateEstimateId);
    setIsLoadingTemplate(true);
    setError(null);

    try {
      const template = await estimatesApi.getEstimateById(templateEstimateId);

      // Build estimate with upload's estimateId + template's category/line item structure
      const estimateFromTemplate = {
        estimateId: uploadResult.estimateId,
        projectName: projectName.trim(),
        categories: template.categories?.map(cat => ({
          ...cat,
          categoryId: generateTempId(),
          lineItems: cat.lineItems?.map(item => ({
            ...item,
            lineItemId: generateTempId(),
            totalCost: item.totalCost,
            unitCost: item.unitCost,
            costSource: 'template',
          })) || []
        })) || [],
        totalCost: template.totalCost || 0,
      };

      setEstimate(estimateFromTemplate);
      setIsLoadingTemplate(false);
      setCurrentStep(STEPS.REVIEW_ESTIMATE);
    } catch (err) {
      console.error('Failed to load template:', err);
      setError(err.message || 'Failed to load template estimate.');
      setIsLoadingTemplate(false);
    }
  }, [uploadResult, projectName, isLoadingTemplate]);

  // ============================================================================
  // Estimate Review
  // ============================================================================
  const handleSaveEstimate = useCallback(async (savedEstimate) => {
    try {
      if (savedEstimate.estimateId) {
        await estimatesApi.updateEstimate(savedEstimate.estimateId, {
          projectName: savedEstimate.projectName || null,
          clientName: savedEstimate.clientName || null,
          location: savedEstimate.location || null,
          categories: savedEstimate.categories,
        });
      }
      setEstimate(savedEstimate);
    } catch (err) {
      setError('Failed to save estimate. Please try again.');
    }
  }, []);

  const handleEstimateNext = useCallback(async (savedEstimate) => {
    try {
      if (savedEstimate.estimateId) {
        await estimatesApi.updateEstimate(savedEstimate.estimateId, {
          projectName: savedEstimate.projectName || null,
          clientName: savedEstimate.clientName || null,
          location: savedEstimate.location || null,
          categories: savedEstimate.categories,
        });
      }
      setEstimate(savedEstimate);
      setCurrentStep(STEPS.UPLOAD_DOCUMENTS);
    } catch (err) {
      setError('Failed to save estimate. Please try again.');
    }
  }, []);

  // ============================================================================
  // Document Upload
  // ============================================================================
  const handleDocumentsChange = useCallback((documents) => {
    setUploadedDocuments(documents);
  }, []);

  const handleContinueToDocProcessing = useCallback(async () => {
    // Allocate the page budget across uploaded docs and trigger processing
    // for each. Documents stay queued until this fires.
    const estimateId = estimate?.estimateId || uploadResult?.estimateId;
    if (estimateId) {
      try {
        await estimatesApi.startDocumentProcessing(estimateId);
      } catch (err) {
        console.warn('Failed to start document processing:', err);
      }
    }
    // Documents process asynchronously in the background — skip waiting
    setSkippedDocuments(true);
    wizardCompletedRef.current = true;
    setCurrentStep(STEPS.COMPLETE);
  }, [estimate, uploadResult]);

  const handleSkipDocProcessing = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setCurrentStep(STEPS.REVIEW_FACTS);
  }, []);

  // ============================================================================
  // Facts Review
  // ============================================================================
  const handleFactsReviewComplete = useCallback(() => {
    wizardCompletedRef.current = true;
    setCurrentStep(STEPS.COMPLETE);
  }, []);

  // ============================================================================
  // Completion
  // ============================================================================
  const handleViewEstimate = useCallback(() => {
    wizardCompletedRef.current = true;
    if (estimate?.estimateId) {
      navigate(`/estimates/${estimate.estimateId}`);
    } else {
      navigate('/estimates');
    }
  }, [navigate, estimate]);

  const handleUploadAnother = useCallback(() => {
    // Reset wizard state
    wizardCompletedRef.current = false;
    setCurrentStep(STEPS.UPLOAD);
    setProjectName('');
    setClientName('');
    setProjectLocation('');
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadResult(null);
    setEstimate(null);
    setUploadedDocuments([]);
    setSkippedDocuments(false);
    setSkippedCsvProcessing(false);
    setError(null);
    setProcessingStatus({ status: 'pending', progress: 0, currentPass: null, currentPassDetail: null });
    setDocProcessingStatus({ total: 0, completed: 0 });
    setAllEstimates([]);
    setAllEstimatesSearch('');
    setIsLoadingAllEstimates(false);
    setIsLoadingTemplate(false);
    setSelectedTemplateId(null);
  }, []);

  // ============================================================================
  // Navigation
  // ============================================================================
  const handleBack = useCallback(() => {
    switch (currentStep) {
      case STEPS.PROJECT_DETAILS:
        // Manual path: back to upload, reset upload state
        if (uploadResult?.estimateId) {
          cleanupPendingEstimate(uploadResult.estimateId);
        }
        setCurrentStep(STEPS.UPLOAD);
        setSelectedFile(null);
        setUploadResult(null);
        setSkippedCsvProcessing(false);
        break;
      case STEPS.CHOOSE_STARTING_POINT:
        setCurrentStep(STEPS.PROJECT_DETAILS);
        setAllEstimates([]);
        setAllEstimatesSearch('');
        setSelectedTemplateId(null);
        break;
      case STEPS.REVIEW_ESTIMATE:
        if (skippedCsvProcessing) {
          setCurrentStep(STEPS.CHOOSE_STARTING_POINT);
          setEstimate(null);
        } else {
          setCurrentStep(STEPS.UPLOAD);
          setSelectedFile(null);
        }
        break;
      case STEPS.UPLOAD_DOCUMENTS:
        setCurrentStep(STEPS.REVIEW_ESTIMATE);
        break;
      case STEPS.DOC_PROCESSING:
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setCurrentStep(STEPS.UPLOAD_DOCUMENTS);
        break;
      case STEPS.REVIEW_FACTS:
        setCurrentStep(STEPS.UPLOAD_DOCUMENTS);
        break;
      default:
        break;
    }
  }, [currentStep, skippedCsvProcessing, uploadResult, cleanupPendingEstimate]);

  const handleCancel = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    // Prevent unmount cleanup from double-deleting
    wizardCompletedRef.current = true;
    // Clean up pending estimate if one was created
    if (uploadResult?.estimateId) {
      cleanupPendingEstimate(uploadResult.estimateId);
    }
    navigate('/estimates');
  }, [navigate, uploadResult, cleanupPendingEstimate]);

  // ============================================================================
  // Step Descriptions
  // ============================================================================
  const getStepDescription = () => {
    switch (currentStep) {
      case STEPS.UPLOAD:
        return 'Upload a CSV or Excel file, or enter your estimate data manually';
      case STEPS.PROJECT_DETAILS:
        return 'Enter your project details';
      case STEPS.CHOOSE_STARTING_POINT:
        return 'Choose to start from scratch or use an existing estimate as a template';
      case STEPS.CSV_PROCESSING:
        return 'Your estimate is being processed and analyzed';
      case STEPS.REVIEW_ESTIMATE:
        return 'Review the extracted categories and line items';
      case STEPS.UPLOAD_DOCUMENTS:
        return 'Add supporting documents to enhance AI pricing accuracy';
      case STEPS.DOC_PROCESSING:
        return 'Extracting key information from your documents';
      case STEPS.REVIEW_FACTS:
        return 'Review and edit extracted document information';
      case STEPS.COMPLETE:
        return 'Your estimate has been successfully imported';
      default:
        return '';
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Cancel
          </Button>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Upload Estimate</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">{getStepDescription()}</p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} skippedDocuments={skippedDocuments} skippedCsvProcessing={skippedCsvProcessing} />

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Project Details (manual path only — shown after upload) */}
      {currentStep === STEPS.PROJECT_DETAILS && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Project Details</CardTitle>
              <Button variant="outline" size="sm" onClick={handleBack}>
                Back
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., Whole Foods Market - Denver"
                  className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Client Name (optional)
                  </label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g., Whole Foods Market Inc."
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Location (optional)
                  </label>
                  <LocationInput
                    value={projectLocation}
                    onChange={setProjectLocation}
                    onValidationChange={setLocationValidated}
                    placeholder="e.g., Denver, CO"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  disabled={!projectName.trim() || (projectLocation.trim() && !locationValidated)}
                  onClick={() => setCurrentStep(STEPS.CHOOSE_STARTING_POINT)}
                >
                  Continue
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload */}
      {currentStep === STEPS.UPLOAD && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Estimate File</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedFile ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRemoveFile}>
                    Remove
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={handleManualEntry}>
                    Fill Out Manually
                  </Button>
                  <Button className="flex-1" onClick={handleUpload}>
                    Upload & Process with AI
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <DropZone onFileSelect={handleFileSelect} isUploading={isUploading} />

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-800" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">or</span>
                  </div>
                </div>

                {/* Skip upload link */}
                <div className="text-center">
                  <button
                    onClick={handleSkipUpload}
                    disabled={isUploading}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    No file to upload? Enter data manually
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step: Choose Starting Point (manual entry path only) */}
      {currentStep === STEPS.CHOOSE_STARTING_POINT && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Choose a Starting Point</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Start with an empty estimate or use an existing estimate as a template
                  for your categories and line items.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleBack}>
                Back
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingTemplate ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
                <span className="ml-3 text-gray-600 dark:text-gray-300">Loading template...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Option 1: Start from scratch */}
                <div
                  onClick={handleStartFromScratch}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors text-center"
                >
                  <svg className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Start from Scratch</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Begin with an empty estimate and add categories and line items manually.
                  </p>
                </div>

                {/* Option 2: Use existing estimate as template */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Or Start from an Existing Estimate
                  </h3>
                  <AllEstimatesTable
                    estimates={allEstimates}
                    search={allEstimatesSearch}
                    onSearchChange={setAllEstimatesSearch}
                    onSelect={handleSelectExistingEstimate}
                    isLoading={isLoadingAllEstimates}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: CSV Processing */}
      {currentStep === STEPS.CSV_PROCESSING && (
        <CSVProcessingProgress
          progress={processingStatus.progress}
          displayProgress={displayProgress}
          fileName={selectedFile?.name}
          onCancel={handleCancelProcessing}
          currentPassDetail={processingStatus.currentPassDetail}
        />
      )}

      {/* Step: Review Estimate */}
      {currentStep === STEPS.REVIEW_ESTIMATE && (
        <div className="space-y-6">
          {/* Editable Project Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
              {!skippedCsvProcessing && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Extracted by AI — edit if needed</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g., Whole Foods Market - Denver"
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g., Whole Foods Market Inc."
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Location
                  </label>
                  <LocationInput
                    value={projectLocation}
                    onChange={setProjectLocation}
                    onValidationChange={setLocationValidated}
                    placeholder="e.g., Denver, CO"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Review Your Estimate</CardTitle>
            </CardHeader>
            <CardContent>
              {estimate ? (
                <EstimateTreeEditor
                  initialEstimate={estimate}
                  projectName={projectName}
                  clientName={clientName}
                  location={projectLocation}
                  onSave={handleSaveEstimate}
                  onNext={handleEstimateNext}
                  onCancel={handleBack}
                  hideAIEstimate
                  hideDocuments
                />
              ) : (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Upload Documents */}
      {currentStep === STEPS.UPLOAD_DOCUMENTS && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Supporting Documents (Optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Add supporting documents like blueprints, surveys, specs, or photos.
                These will be analyzed to improve AI pricing accuracy for future estimates.
              </p>
              <SupportingDocumentsUploader
                estimateId={estimate?.estimateId || uploadResult?.uploadId}
                onDocumentsChange={handleDocumentsChange}
              />
            </CardContent>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
            <Button
              onClick={handleContinueToDocProcessing}
              disabled={uploadedDocuments.some((d) => d.status === 'uploading')}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: Document Processing */}
      {currentStep === STEPS.DOC_PROCESSING && (
        <DocProcessingProgress
          total={docProcessingStatus.total}
          completed={docProcessingStatus.completed}
          onSkip={handleSkipDocProcessing}
        />
      )}

      {/* Step 6: Review Facts */}
      {currentStep === STEPS.REVIEW_FACTS && (
        <div className="space-y-6">
          <DocumentFactsReview
            estimateId={estimate?.estimateId || uploadResult?.uploadId}
            documents={uploadedDocuments}
            onComplete={handleFactsReviewComplete}
            onBack={handleBack}
          />
        </div>
      )}

      {/* Step 7: Complete */}
      {currentStep === STEPS.COMPLETE && (
        <UploadComplete
          estimate={estimate}
          documentCount={uploadedDocuments.length}
          onViewEstimate={handleViewEstimate}
          onUploadAnother={handleUploadAnother}
        />
      )}
    </div>
  );
}

export default EstimateUploadWizard;
